import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useImportProjectMutation } from "@app/features/project/projectApiSlice";
import { ImportProjectResponse } from "@app/features/project/projectTypes";
import useErrorMessage from "@utils/useErrorMessage";
import type { ErrorType } from "@typedefs/interfaces";

interface UseImportProjectReturn {
	handleImportClick: () => void;
	isLoadingImport: boolean;
	importErrorMsg: string;
	isImportErrorMessageOpen: boolean;
	closeImportErrorMessage: () => void;
	isImportExistsDialogOpen: boolean;
	existingProject: ImportProjectResponse | null;
	handleImportForceCopy: () => void;
	handleCancelImportExists: () => void;
}

const useImportProject = (refetch: () => void): UseImportProjectReturn => {
	const navigate = useNavigate();
	const [importProject, { isLoading: isLoadingImport, isError, error }] = useImportProjectMutation();
	const [isImportErrorMessageOpen, setIsImportErrorMessageOpen] = useState(false);
	const [isImportExistsDialogOpen, setIsImportExistsDialogOpen] = useState(false);
	const [existingProject, setExistingProject] = useState<ImportProjectResponse | null>(null);
	const pendingDataRef = useRef<Record<string, unknown> | null>(null);
	const { errorMsg } = useErrorMessage({ isError, error: error as ErrorType | null });

	const closeImportErrorMessage = useCallback(() => {
		setIsImportErrorMessageOpen(false);
	}, []);

	useEffect(() => {
		if (errorMsg) {
			setIsImportErrorMessageOpen(true);
		}
	}, [errorMsg]);

	const processImport = useCallback(async (data: Record<string, unknown>, forceCopy: boolean) => {
		try {
			const result = await importProject({ data, forceCopy }).unwrap() as ImportProjectResponse;
			if (result.exists) {
				setExistingProject(result);
				setIsImportExistsDialogOpen(true);
				pendingDataRef.current = data;
			} else {
				await refetch();
				navigate(`/project/${result.projectId}/activities`);
			}
		} catch {
			// Error handled by useErrorMessage
		}
	}, [importProject, refetch, navigate]);

	const handleImportClick = useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".exactp";
		input.onchange = async (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (!file) return;

			try {
				const text = await file.text();
				const data = JSON.parse(text);
				await processImport(data, false);
			} catch {
				setIsImportErrorMessageOpen(true);
			}
		};
		input.click();
	}, [processImport]);

	const handleImportForceCopy = useCallback(async () => {
		setIsImportExistsDialogOpen(false);
		const data = pendingDataRef.current;
		if (data) {
			pendingDataRef.current = null;
			await processImport(data, true);
		}
	}, [processImport]);

	const handleCancelImportExists = useCallback(() => {
		setIsImportExistsDialogOpen(false);
		setExistingProject(null);
		pendingDataRef.current = null;
	}, []);

	return {
		handleImportClick,
		isLoadingImport,
		importErrorMsg: errorMsg,
		isImportErrorMessageOpen,
		closeImportErrorMessage,
		isImportExistsDialogOpen,
		existingProject,
		handleImportForceCopy,
		handleCancelImportExists,
	};
};

export default useImportProject;
